import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Globe, Link2, MoreVertical, Pencil, Smartphone } from 'lucide-react';
import {
  getCustomer,
  listCustomerDevices,
  listCustomers,
  maskImei,
  unarchiveCustomer,
  updateCustomer,
  type CustomerDetail,
  type DuplicateCustomerCard,
  type DuplicateGroup,
} from '@/lib/api/customers.js';
import { MergeCustomersModal } from '@/components/customers/MergeCustomersModal.js';
import {
  AnonymizeCustomerModal,
  ArchiveCustomerModal,
  DeleteCustomerModal,
  useCustomerRefs,
} from '@/components/customers/CustomerArchiveModals.js';
import { updateCustomerStatus } from '@/lib/api/customer-status-upsell.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { CustomerStatusBadge } from '@/components/common/CustomerStatusBadge.js';
import { CloneTicketModal } from '@/components/tickets/CloneTicketModal.js';
import { DeleteTicketModal } from '@/components/tickets/DeleteTicketModal.js';
import { useAuthStore } from '@/stores/auth.js';
import { isManagerRole } from '@/lib/permissions.js';
import { listTickets } from '@/lib/api/tickets.js';
import {
  createCustomerPortal,
  revokeCustomerPortal,
  sendCustomerPortalReset,
  updateCustomerPortalSettings,
} from '@/lib/api/portal-staff.js';
import { SlideOver } from '@/components/SlideOver.js';
import { CustomerForm, type CustomerFormValues } from './CustomerForm.js';
import {
  CommPreferencesCard,
  CommPreferencesSlideOver,
  type CommPreferencesValues,
} from '@/components/customers/CommPreferencesSection.js';
import { formatCurrency, formatDate, formatDateTime, prettyLabel } from '@/lib/format.js';
import { StatusBadge } from '@/components/tickets/StatusBadge.js';
import { CustomerSubscriptionsSection } from '@/components/customers/CustomerSubscriptionsSection.js';
import { CustomerCreditPointsSection } from '@/components/customers/CustomerCreditPointsSection.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

function toMergeCard(c: CustomerDetail | {
  id: string;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  customerStatus?: string;
  statusReason?: string | null;
  createdAt?: string;
  importedFrom?: string | null;
  totalTickets?: number;
  totalSpend?: string;
  loyaltyPointsBalance?: number;
  storeCreditBalance?: string;
  lastVisitAt?: string | null;
}): DuplicateCustomerCard {
  return {
    id: c.id,
    firstName: c.firstName,
    lastName: c.lastName ?? null,
    email: c.email ?? null,
    phone: c.phone ?? null,
    address: c.address ?? null,
    city: c.city ?? null,
    state: c.state ?? null,
    zip: c.zip ?? null,
    customerStatus: c.customerStatus ?? 'normal',
    statusReason: c.statusReason ?? null,
    createdAt: c.createdAt ?? new Date(0).toISOString(),
    importedFrom: c.importedFrom ?? null,
    ticketCount: c.totalTickets ?? 0,
    totalSpend: c.totalSpend ?? '0.00',
    loyaltyPoints: c.loyaltyPointsBalance ?? 0,
    storeCredit: c.storeCreditBalance ?? '0.00',
    lastVisitAt: c.lastVisitAt ?? null,
  };
}
function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-bold text-slate-950">{value}</div>
    </div>
  );
}

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [commOpen, setCommOpen] = useState(false);
  const [statusModal, setStatusModal] = useState<'vip' | 'flagged' | 'edit' | 'remove' | null>(null);
  const [statusReason, setStatusReason] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [cloneTarget, setCloneTarget] = useState<{ id: string; ticketNumber: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    ticketNumber: string;
    status: string;
    total: string;
    paidTotal?: string;
    balanceDue?: string;
    importedFrom?: string | null;
  } | null>(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [mergeSearchOpen, setMergeSearchOpen] = useState(false);
  const [mergeQuery, setMergeQuery] = useState('');
  const [mergeGroup, setMergeGroup] = useState<DuplicateGroup | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteCustomerOpen, setDeleteCustomerOpen] = useState(false);
  const [anonymizeOpen, setAnonymizeOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const canEditStatus = isManagerRole(user?.role ?? '');
  const canAdjustBalances = canEditStatus;
  const canDeleteTickets =
    user?.role === 'tenant_admin' || user?.role === 'platform_admin';
  const canHardDeleteCustomer =
    user?.role === 'tenant_admin' || user?.role === 'platform_admin';
  const refsQuery = useCustomerRefs(id, canEditStatus || canHardDeleteCustomer);

  const customerQuery = useQuery({
    queryKey: ['customer', id],
    queryFn: () => getCustomer(id!),
    enabled: Boolean(id),
  });

  const devicesQuery = useQuery({
    queryKey: ['customer-devices', id],
    queryFn: () => listCustomerDevices(id!),
    enabled: Boolean(id),
  });

  const crossStoreTicketsQuery = useQuery({
    queryKey: ['customer-tickets-all-stores', id],
    queryFn: () => listTickets({ customerId: id!, page: 1, pageSize: 50 }),
    enabled: Boolean(id),
  });

  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const vipLabel = storeQuery.data?.vipLabel ?? 'VIP';
  const flaggedLabel = storeQuery.data?.flaggedLabel ?? 'Flagged';

  const mergeSearchQuery = useQuery({
    queryKey: ['customer-merge-search', mergeQuery],
    queryFn: () => listCustomers({ q: mergeQuery, page: 1, pageSize: 15 }),
    enabled: mergeSearchOpen && mergeQuery.trim().length >= 2,
  });

  const statusMutation = useMutation({
    mutationFn: (body: { status: 'normal' | 'vip' | 'flagged'; reason?: string | null; notes?: string | null }) =>
      updateCustomerStatus(id!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customer', id] });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      setStatusModal(null);
      setStatusReason('');
      setStatusNotes('');
      toastSuccess('Customer status updated');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (values: CustomerFormValues) => updateCustomer(id!, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setEditOpen(false);
      toastSuccess('Customer updated successfully');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const commMutation = useMutation({
    mutationFn: (values: CommPreferencesValues) => updateCustomer(id!, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
      setCommOpen(false);
      toastSuccess('Communication preferences saved');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const customer = customerQuery.data;

  if (customerQuery.isLoading) {
    return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading customer...</div>;
  }

  if (!customer) {
    return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Customer not found.</div>;
  }

  const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ');

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={() => navigate('/customers')}
        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-950"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to customers
      </button>

      {customer.archivedAt ? (
        <div className="rounded-xl border border-slate-300 bg-slate-100 px-4 py-3 text-sm text-slate-800">
          <p className="font-semibold">
            Archived on {formatDate(customer.archivedAt)}
            {customer.archivedByName ? ` by ${customer.archivedByName}` : ''}
          </p>
          {customer.archiveReason ? (
            <p className="mt-1 text-slate-600">Reason: {customer.archiveReason}</p>
          ) : null}
          {canEditStatus ? (
            <button
              type="button"
              className="mt-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800"
              onClick={() => {
                void unarchiveCustomer(customer.id)
                  .then(() => {
                    void queryClient.invalidateQueries({ queryKey: ['customer', id] });
                    void queryClient.invalidateQueries({ queryKey: ['customers'] });
                    toastSuccess('Customer restored');
                  })
                  .catch((error) => toastError(parseApiError(error).message));
              }}
            >
              Unarchive
            </button>
          ) : null}
        </div>
      ) : null}

      {customer.anonymizedAt ? (
        <div className="rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-800">
          This customer&apos;s personal information was removed on {formatDate(customer.anonymizedAt)}.
          Transaction history is retained.
        </div>
      ) : null}

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-bold text-slate-950">{name}</h2>
              <CustomerStatusBadge
                status={customer.customerStatus}
                vipLabel={vipLabel}
                flaggedLabel={flaggedLabel}
                reason={customer.statusReason ?? null}
                size="md"
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
              <span>{customer.phone ?? 'No phone'}</span>
              <span>{customer.email ?? 'No email'}</span>
            </div>
            {(customer.sharedPhoneCustomers?.length ?? 0) > 0 ? (
              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                <p className="font-semibold">📞 Shared number</p>
                <p className="mt-1 text-slate-600">Also used by:</p>
                <ul className="mt-1 space-y-1">
                  {customer.sharedPhoneCustomers!.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-2">
                      <span>
                        {[p.firstName, p.lastName].filter(Boolean).join(' ')} ({p.totalTickets}{' '}
                        tickets)
                      </span>
                      <Link
                        to={`/customers/${p.id}`}
                        className="text-xs font-semibold text-primary hover:underline"
                      >
                        View
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Pencil className="h-4 w-4" />
            Edit
          </button>
        </div>

        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">Customer status</p>
              <div className="mt-1 flex items-center gap-2">
                <CustomerStatusBadge
                  status={customer.customerStatus}
                  vipLabel={vipLabel}
                  flaggedLabel={flaggedLabel}
                  reason={customer.statusReason ?? null}
                />
                {!customer.customerStatus || customer.customerStatus === 'normal' ? (
                  <span className="text-sm text-slate-500">Normal</span>
                ) : null}
              </div>
            </div>
            {canEditStatus ? (
              <div className="flex flex-wrap items-start gap-2">
                {customer.customerStatus !== 'vip' ? (
                  <button type="button" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900" onClick={() => { setStatusReason(''); setStatusNotes(''); setStatusModal('vip'); }}>Mark VIP</button>
                ) : null}
                {customer.customerStatus !== 'flagged' ? (
                  <button type="button" className="rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-800" onClick={() => { setStatusReason(''); setStatusNotes(''); setStatusModal('flagged'); }}>Flag</button>
                ) : null}
                {customer.customerStatus && customer.customerStatus !== 'normal' ? (
                  <>
                    <button type="button" className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700" onClick={() => { setStatusReason(customer.statusReason ?? ''); setStatusNotes(customer.statusNotes ?? ''); setStatusModal('edit'); }}>Edit</button>
                    <button type="button" className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700" onClick={() => setStatusModal('remove')}>Remove</button>
                  </>
                ) : null}
                <div className="relative">
                  <button
                    type="button"
                    className="rounded-lg border border-slate-300 bg-white p-1.5 text-slate-700 hover:bg-slate-50"
                    aria-label="More actions"
                    onClick={() => setActionsOpen((o) => !o)}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                  {actionsOpen ? (
                    <div className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                      <button
                        type="button"
                        className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                        onClick={() => {
                          setActionsOpen(false);
                          setMergeQuery('');
                          setMergeSearchOpen(true);
                        }}
                      >
                        Merge with another customer
                      </button>
                      <Link
                        to="/settings/duplicate-customers"
                        className="block px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
                        onClick={() => setActionsOpen(false)}
                      >
                        Open duplicate scanner…
                      </Link>
                      <div className="my-1 border-t border-slate-100" />
                      {customer.archivedAt ? (
                        <button
                          type="button"
                          className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                          onClick={() => {
                            setActionsOpen(false);
                            void unarchiveCustomer(customer.id)
                              .then(() => {
                                void queryClient.invalidateQueries({ queryKey: ['customer', id] });
                                void queryClient.invalidateQueries({ queryKey: ['customers'] });
                                toastSuccess('Customer restored');
                              })
                              .catch((error) => toastError(parseApiError(error).message));
                          }}
                        >
                          Unarchive
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                          onClick={() => {
                            setActionsOpen(false);
                            setArchiveOpen(true);
                          }}
                        >
                          Archive
                        </button>
                      )}
                      {canHardDeleteCustomer && !customer.anonymizedAt ? (
                        <button
                          type="button"
                          className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                          onClick={() => {
                            setActionsOpen(false);
                            setAnonymizeOpen(true);
                          }}
                        >
                          Anonymize
                        </button>
                      ) : null}
                      {canHardDeleteCustomer ? (
                        <button
                          type="button"
                          className="block w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                          title={
                            refsQuery.data && !refsQuery.data.canHardDelete
                              ? 'Linked records block delete — you can still open to Archive Instead'
                              : undefined
                          }
                          onClick={() => {
                            setActionsOpen(false);
                            setDeleteCustomerOpen(true);
                          }}
                        >
                          Delete
                          {refsQuery.data && !refsQuery.data.canHardDelete ? (
                            <span className="mt-0.5 block text-[11px] font-normal text-slate-500">
                              Blocked — has linked records
                            </span>
                          ) : null}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500">View only — manager role required to change status</p>
            )}

          </div>
          {(customer.statusReason || customer.statusNotes || customer.statusSetBy || customer.statusSetAt) ? (
            <dl className="mt-3 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
              {customer.statusReason ? (<div><dt className="text-xs uppercase tracking-wide text-slate-400">Reason</dt><dd>{customer.statusReason}</dd></div>) : null}
              {customer.statusNotes ? (<div><dt className="text-xs uppercase tracking-wide text-slate-400">Notes</dt><dd>{customer.statusNotes}</dd></div>) : null}
              {customer.statusSetBy ? (<div><dt className="text-xs uppercase tracking-wide text-slate-400">Set by</dt><dd>{customer.statusSetBy}</dd></div>) : null}
              {customer.statusSetAt ? (<div><dt className="text-xs uppercase tracking-wide text-slate-400">Set at</dt><dd>{formatDateTime(customer.statusSetAt)}</dd></div>) : null}
            </dl>
          ) : null}
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Total Tickets" value={customer.totalTickets} />
        <Stat label="Total Spend" value={formatCurrency(customer.totalSpend)} />
        <Stat label="Points Balance" value={customer.loyaltyPointsBalance ?? 0} />
        <Stat label="Store Credit" value={formatCurrency(customer.storeCreditBalance ?? '0')} />
      </section>

      <CustomerCreditPointsSection
        customerId={customer.id}
        pointsBalance={customer.loyaltyPointsBalance ?? 0}
        storeCreditBalance={customer.storeCreditBalance}
        canAdjust={canAdjustBalances}
      />

      <ReferralSection customer={customer} onOpenCustomer={(id) => navigate(`/customers/${id}`)} />

      <CommPreferencesCard customer={customer} onEdit={() => setCommOpen(true)} />

      <CustomerSubscriptionsSection customerId={customer.id} />

      <PortalAccessSection customerId={customer.id} customer={customer} />

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-950">Visit History (All Stores)</h3>
          <p className="text-sm text-slate-500">Tickets across all locations for this customer</p>
        </div>
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">Ticket #</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Total</th>
              <th className="px-5 py-3">Created</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(crossStoreTicketsQuery.data?.data ?? []).map((ticket) => (
              <tr key={ticket.id} className="hover:bg-slate-50">
                <td
                  className="cursor-pointer px-5 py-4 font-semibold text-primary"
                  onClick={() => navigate(`/tickets/${ticket.id}`)}
                >
                  {ticket.ticketNumber}
                </td>
                <td className="cursor-pointer px-5 py-4" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                  <StatusBadge status={ticket.status} />
                </td>
                <td
                  className="cursor-pointer px-5 py-4 text-slate-600"
                  onClick={() => navigate(`/tickets/${ticket.id}`)}
                >
                  {formatCurrency(ticket.total ?? '0')}
                </td>
                <td
                  className="cursor-pointer px-5 py-4 text-slate-600"
                  onClick={() => navigate(`/tickets/${ticket.id}`)}
                >
                  {formatDateTime(ticket.createdAt)}
                </td>
                <td className="px-5 py-4 text-right">
                  <div className="inline-flex items-center gap-3">
                    <button
                      type="button"
                      className="text-sm font-semibold text-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/tickets/${ticket.id}`);
                      }}
                    >
                      View
                    </button>
                    {canDeleteTickets ? (
                      <button
                        type="button"
                        className="text-sm font-semibold text-red-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget({
                            id: ticket.id,
                            ticketNumber: ticket.ticketNumber,
                            status: ticket.status,
                            total: String(ticket.total ?? '0'),
                            ...(ticket.paidTotal != null
                              ? { paidTotal: String(ticket.paidTotal) }
                              : {}),
                            ...('importedFrom' in ticket && ticket.importedFrom != null
                              ? { importedFrom: ticket.importedFrom }
                              : {}),
                            ...('balanceDue' in ticket && ticket.balanceDue != null
                              ? { balanceDue: String(ticket.balanceDue) }
                              : {}),
                          });
                        }}
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
            {!crossStoreTicketsQuery.isLoading && !(crossStoreTicketsQuery.data?.data ?? []).length ? (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-slate-500">
                  No cross-store ticket history yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-950">Recent Tickets (This Store)</h3>
        </div>
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">Ticket #</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Total</th>
              <th className="px-5 py-3">Created</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {customer.recentTickets.map((ticket) => (
              <tr key={ticket.id} className="hover:bg-slate-50">
                <td className="px-5 py-4 font-semibold text-primary cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>{ticket.ticketNumber}</td>
                <td className="px-5 py-4 cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}><StatusBadge status={ticket.status} /></td>
                <td className="px-5 py-4 text-slate-600 cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>{formatCurrency(ticket.total)}</td>
                <td className="px-5 py-4 text-slate-600 cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>{formatDateTime(ticket.createdAt)}</td>
                <td className="px-5 py-4 text-right">
                  <div className="inline-flex items-center gap-3">
                    <button
                      type="button"
                      className="text-sm font-semibold text-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/tickets/${ticket.id}`);
                      }}
                    >
                      View
                    </button>
                    {canEditStatus ? (
                      <button
                        type="button"
                        className="text-sm font-semibold text-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCloneTarget({ id: ticket.id, ticketNumber: ticket.ticketNumber });
                        }}
                      >
                        Clone
                      </button>
                    ) : null}
                    {canDeleteTickets ? (
                      <button
                        type="button"
                        className="text-sm font-semibold text-red-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget({
                            id: ticket.id,
                            ticketNumber: ticket.ticketNumber,
                            status: ticket.status,
                            total: ticket.total,
                            ...(ticket.paidTotal != null ? { paidTotal: ticket.paidTotal } : {}),
                            ...(ticket.importedFrom != null
                              ? { importedFrom: ticket.importedFrom }
                              : {}),
                          });
                        }}
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
            {customer.recentTickets.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-slate-500">
                  No recent tickets.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-950">Devices</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {(devicesQuery.data ?? []).map((device, index) => {
            const ticketsHref = (() => {
              const params = new URLSearchParams();
              params.set('customerId', customer.id);
              if (device.modelId) params.set('modelId', device.modelId);
              else if (device.customModelName) params.set('customModel', device.customModelName);
              return `/tickets?${params.toString()}`;
            })();
            return (
              <div key={`${device.makeId ?? 'custom'}-${device.modelId ?? index}`} className="px-5 py-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-3">
                      <Smartphone className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
                      <div>
                        <div className="font-semibold text-slate-950">{device.displayName}</div>
                        <div className="text-sm text-slate-500">
                          {device.totalRepairs} repair{device.totalRepairs === 1 ? '' : 's'}
                          {device.lastTicketDate
                            ? ` · Last: ${formatDate(device.lastTicketDate.slice(0, 10))}`
                            : ''}
                        </div>
                        {device.lastImei ? <DeviceImeiReveal imei={device.lastImei} /> : null}
                      </div>
                    </div>
                    {(device.recentTickets?.length ?? 0) > 0 ? (
                      <div className="mt-3 ml-9">
                        <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
                          Repair History
                        </div>
                        <ul className="space-y-1 text-sm text-slate-700">
                          {device.recentTickets!.slice(0, 5).map((t) => (
                            <li key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                              <Link to={`/tickets/${t.id}`} className="font-semibold text-primary hover:underline">
                                {t.ticketNumber}
                              </Link>
                              <span>{t.repairSummary}</span>
                              <span className="text-slate-400">
                                {formatDate(t.date.slice(0, 10))}
                              </span>
                              {canEditStatus ? (
                                <button type="button" className="text-xs font-semibold text-primary" onClick={() => setCloneTarget({ id: t.id, ticketNumber: t.ticketNumber })}>[Clone →]</button>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-2 md:items-end">
                    <button
                      type="button"
                      className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      onClick={() => {
                        const params = new URLSearchParams();
                        params.set('customerId', customer.id);
                        if (device.makeId) params.set('makeId', device.makeId);
                        if (device.modelId) params.set('modelId', device.modelId);
                        navigate(`/tickets/quick?${params.toString()}`);
                      }}
                    >
                      New Ticket for this Device
                    </button>
                    <Link
                      to={ticketsHref}
                      className="text-sm font-semibold text-primary hover:underline"
                    >
                      View All Tickets for this device →
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
          {devicesQuery.isLoading ? (
            <div className="space-y-3 px-5 py-4">
              {[0, 1].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />
              ))}
            </div>
          ) : null}
          {!devicesQuery.isLoading && (devicesQuery.data?.length ?? 0) === 0 ? (
            <div className="px-5 py-8 text-center text-slate-500">No device history yet.</div>
          ) : null}
        </div>
      </section>

      <SlideOver title="Edit Customer" open={editOpen} onClose={() => setEditOpen(false)}>
        {editOpen ? (
          <CustomerForm
            key={customer.id}
            initial={customer}
            excludeCustomerId={id ?? null}
            submitLabel="Save changes"
            loading={updateMutation.isPending}
            onSubmit={(values) => updateMutation.mutate(values)}
          />
        ) : null}
      </SlideOver>

      <CommPreferencesSlideOver
        open={commOpen}
        onClose={() => setCommOpen(false)}
        customer={customer}
        loading={commMutation.isPending}
        onSubmit={(values) => commMutation.mutate(values)}
      />

      {statusModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-950">
              {statusModal === 'vip' ? `Mark ${vipLabel}` : statusModal === 'flagged' ? `Mark ${flaggedLabel}` : statusModal === 'edit' ? 'Edit status' : 'Remove status'}
            </h3>
            {statusModal === 'remove' ? (
              <p className="mt-2 text-sm text-slate-600">Clear VIP/flagged status and return this customer to normal.</p>
            ) : (
              <div className="mt-4 space-y-3">
                <label className="block text-sm">
                  <span className="font-medium text-slate-700">Reason</span>
                  <input className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={statusReason} onChange={(e) => setStatusReason(e.target.value)} />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-slate-700">Notes</span>
                  <textarea className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" rows={3} value={statusNotes} onChange={(e) => setStatusNotes(e.target.value)} />
                </label>
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="rounded-lg border border-slate-200 px-4 py-2 text-sm" onClick={() => setStatusModal(null)}>Cancel</button>
              <button
                type="button"
                disabled={statusMutation.isPending}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                onClick={() => {
                  if (statusModal === 'remove') {
                    statusMutation.mutate({ status: 'normal', reason: null, notes: null });
                    return;
                  }
                  const nextStatus = statusModal === 'edit'
                    ? ((customer.customerStatus === 'flagged' ? 'flagged' : 'vip') as 'vip' | 'flagged')
                    : statusModal;
                  statusMutation.mutate({
                    status: nextStatus,
                    reason: statusReason.trim() || null,
                    notes: statusNotes.trim() || null,
                  });
                }}
              >
                {statusMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {cloneTarget ? (
        <CloneTicketModal
          ticketId={cloneTarget.id}
          ticketNumber={cloneTarget.ticketNumber}
          currentCustomerId={customer.id}
          open
          onClose={() => setCloneTarget(null)}
        />
      ) : null}

      <DeleteTicketModal
        open={Boolean(deleteTarget)}
        ticket={deleteTarget}
        {...(customer
          ? {
              customerName: [customer.firstName, customer.lastName].filter(Boolean).join(' '),
            }
          : {})}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => {
          setDeleteTarget(null);
          void queryClient.invalidateQueries({ queryKey: ['customer', id] });
          void queryClient.invalidateQueries({ queryKey: ['customer-tickets-all-stores', id] });
          void queryClient.invalidateQueries({ queryKey: ['tickets'] });
          void queryClient.invalidateQueries({ queryKey: ['ticket-counts'] });
        }}
      />

      {mergeSearchOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
            <h2 className="text-lg font-bold text-slate-950">Merge with another customer</h2>
            <p className="mt-1 text-sm text-slate-500">
              Search for the duplicate record to absorb into this customer (or choose either as
              survivor in the next step).
            </p>
            <input
              className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="Name, phone, or email…"
              value={mergeQuery}
              autoFocus
              onChange={(e) => setMergeQuery(e.target.value)}
            />
            <ul className="mt-3 max-h-64 space-y-1 overflow-y-auto">
              {(mergeSearchQuery.data?.data ?? [])
                .filter((c) => c.id !== customer.id)
                .map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50"
                      onClick={() => {
                        const primary = toMergeCard(customer);
                        const other = toMergeCard(c);
                        setMergeGroup({
                          groupId: `manual-${customer.id}-${c.id}`,
                          strategy: 'exact_phone',
                          confidence: 'medium',
                          matchedOn: 'manual selection',
                          customers: [primary, other],
                        });
                        setMergeSearchOpen(false);
                      }}
                    >
                      <span className="font-semibold">
                        {c.firstName} {c.lastName}
                      </span>
                      <span className="block text-xs text-slate-500">
                        {c.email ?? c.phone ?? 'No contact'} · {c.totalTickets} tickets ·{' '}
                        {formatCurrency(c.totalSpend)}
                      </span>
                    </button>
                  </li>
                ))}
              {mergeQuery.trim().length >= 2 &&
              !mergeSearchQuery.isFetching &&
              (mergeSearchQuery.data?.data ?? []).filter((c) => c.id !== customer.id).length ===
                0 ? (
                <li className="px-3 py-2 text-sm text-slate-500">No matches</li>
              ) : null}
            </ul>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold"
                onClick={() => setMergeSearchOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {mergeGroup ? (
        <MergeCustomersModal
          group={mergeGroup}
          onClose={() => setMergeGroup(null)}
          onMerged={({ primaryId }) => {
            setMergeGroup(null);
            void queryClient.invalidateQueries({ queryKey: ['customers'] });
            if (primaryId !== id) {
              void navigate(`/customers/${primaryId}`);
            } else {
              void queryClient.invalidateQueries({ queryKey: ['customer', id] });
            }
          }}
        />
      ) : null}

      <ArchiveCustomerModal
        open={archiveOpen}
        customer={customer}
        onClose={() => setArchiveOpen(false)}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: ['customer', id] });
          void queryClient.invalidateQueries({ queryKey: ['customers'] });
        }}
      />
      <DeleteCustomerModal
        open={deleteCustomerOpen}
        customer={customer}
        onClose={() => setDeleteCustomerOpen(false)}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: ['customers'] });
          navigate('/customers');
        }}
        onArchiveInstead={() => setArchiveOpen(true)}
      />
      <AnonymizeCustomerModal
        open={anonymizeOpen}
        customer={customer}
        onClose={() => setAnonymizeOpen(false)}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: ['customer', id] });
          void queryClient.invalidateQueries({ queryKey: ['customers'] });
        }}
      />
    </div>
  );
}

function DeviceImeiReveal({ imei }: { imei: string }) {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!revealed) return;
    const t = window.setTimeout(() => setRevealed(false), 10_000);
    return () => window.clearTimeout(t);
  }, [revealed]);

  return (
    <button
      type="button"
      className="mt-1 inline-flex items-center gap-1 font-mono text-xs text-slate-500 hover:text-slate-800"
      onClick={() => setRevealed((v) => !v)}
      title={revealed ? 'Hide IMEI' : 'Reveal IMEI'}
    >
      IMEI: {revealed ? imei : maskImei(imei)}
      {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
    </button>
  );
}

function ReferralSection({
  customer,
  onOpenCustomer,
}: {
  customer: {
    acquisitionSource?: string | null;
    acquisitionSourceDetail?: string | null;
    referredByCustomerId?: string | null;
    referredByCustomerName?: string | null;
    referredCustomers?: Array<{ id: string; name: string; createdAt: string }>;
  };
  onOpenCustomer: (id: string) => void;
}) {
  const hasAnyData =
    Boolean(customer.acquisitionSource) ||
    Boolean(customer.referredByCustomerId) ||
    Boolean(customer.referredCustomers?.length);

  if (!hasAnyData) return null;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <Link2 className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold text-slate-950">Referral &amp; Acquisition</h3>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {customer.acquisitionSource ? (
          <div>
            <p className="text-xs font-medium text-slate-500">How they heard about us</p>
            <p className="mt-1 text-sm font-semibold text-slate-950">
              {prettyLabel(customer.acquisitionSource)}
              {customer.acquisitionSourceDetail ? ` — ${customer.acquisitionSourceDetail}` : ''}
            </p>
          </div>
        ) : null}
        {customer.referredByCustomerId ? (
          <div>
            <p className="text-xs font-medium text-slate-500">Referred by</p>
            <button
              type="button"
              className="mt-1 text-sm font-semibold text-primary hover:underline"
              onClick={() => onOpenCustomer(customer.referredByCustomerId!)}
            >
              {customer.referredByCustomerName ?? 'View customer'}
            </button>
          </div>
        ) : null}
      </div>

      {customer.referredCustomers?.length ? (
        <div className="mt-4">
          <p className="text-xs font-medium text-slate-500">
            Customers referred by this person ({customer.referredCustomers.length})
          </p>
          <ul className="mt-2 divide-y divide-slate-100">
            {customer.referredCustomers.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-2 text-sm">
                <button
                  type="button"
                  className="font-medium text-primary hover:underline"
                  onClick={() => onOpenCustomer(r.id)}
                >
                  {r.name}
                </button>
                <span className="text-xs text-slate-500">Joined {formatDate(r.createdAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function PortalAccessSection({
  customerId,
  customer,
}: {
  customerId: string;
  customer: {
    portalEnabled?: boolean;
    portalEmail?: string | null;
    portalLastLogin?: string | null;
    portalCanCreateTickets?: boolean;
    portalCreatedAt?: string | null;
    email?: string | null;
  };
}) {
  const queryClient = useQueryClient();
  const hasPortal = Boolean(customer.portalEnabled && customer.portalEmail);
  const [createdCreds, setCreatedCreds] = useState<{
    portalEmail: string;
    tempPassword: string;
    tenantSlug: string;
    loginUrl: string;
  } | null>(null);

  const createMutation = useMutation({
    mutationFn: () => createCustomerPortal(customerId, { canCreateTickets: false }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
      setCreatedCreds({
        portalEmail: data.portalEmail,
        tempPassword: data.tempPassword,
        tenantSlug: data.tenantSlug,
        loginUrl: data.loginUrl,
      });
      toastSuccess('Portal account created — welcome email sent');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const settingsMutation = useMutation({
    mutationFn: (body: { canCreateTickets?: boolean; portalEnabled?: boolean }) =>
      updateCustomerPortalSettings(customerId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
      toastSuccess('Portal settings updated');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => sendCustomerPortalReset(customerId),
    onSuccess: () => toastSuccess('Password reset email sent'),
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: () => revokeCustomerPortal(customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
      setCreatedCreds(null);
      toastSuccess('Portal access revoked');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <Globe className="h-5 w-5 text-[#1B4F8A]" />
        <h3 className="text-lg font-semibold text-slate-950">Customer Portal</h3>
      </div>

      {createdCreds ? (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
          <p className="font-semibold text-amber-900">Save these credentials (shown once)</p>
          <dl className="mt-2 space-y-1 text-amber-950">
            <div>
              <dt className="inline text-amber-700">Login: </dt>
              <dd className="inline">
                <a
                  href={createdCreds.loginUrl}
                  className="font-medium underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  {createdCreds.loginUrl}
                </a>
              </dd>
            </div>
            <div>
              <dt className="inline text-amber-700">Email: </dt>
              <dd className="inline font-medium">{createdCreds.portalEmail}</dd>
            </div>
            <div>
              <dt className="inline text-amber-700">Temp password: </dt>
              <dd className="inline font-mono font-bold">{createdCreds.tempPassword}</dd>
            </div>
            {createdCreds.tenantSlug ? (
              <div>
                <dt className="inline text-amber-700">Shop ID: </dt>
                <dd className="inline font-mono font-medium">{createdCreds.tenantSlug}</dd>
              </div>
            ) : null}
          </dl>
          <button
            type="button"
            className="mt-3 text-xs font-semibold text-amber-800 underline"
            onClick={() => {
              void navigator.clipboard.writeText(
                `Login: ${createdCreds.loginUrl}\nEmail: ${createdCreds.portalEmail}\nPassword: ${createdCreds.tempPassword}${createdCreds.tenantSlug ? `\nShop ID: ${createdCreds.tenantSlug}` : ''}`,
              );
              toastSuccess('Copied to clipboard');
            }}
          >
            Copy all
          </button>
        </div>
      ) : null}

      {!hasPortal ? (
        <div>
          <p className="text-sm text-slate-500">No portal account</p>
          <button
            type="button"
            disabled={createMutation.isPending || !customer.email}
            title={!customer.email ? 'Customer needs an email address first' : undefined}
            onClick={() => createMutation.mutate()}
            className="mt-3 rounded-xl bg-[#1B4F8A] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            Create Portal Account
          </button>
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="font-semibold text-green-700">✓ Active</p>
          <p>
            Email: <span className="font-medium">{customer.portalEmail}</span>
          </p>
          <p>
            Last login:{' '}
            {customer.portalLastLogin ? formatDateTime(customer.portalLastLogin) : 'Never'}
          </p>
          <label className="flex items-center gap-2">
            <span>Can create tickets:</span>
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold"
              onClick={() =>
                settingsMutation.mutate({
                  canCreateTickets: !customer.portalCanCreateTickets,
                })
              }
            >
              {customer.portalCanCreateTickets ? 'Yes' : 'No'} — toggle
            </button>
          </label>
          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="button"
              onClick={() => resetMutation.mutate()}
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
            >
              Send Password Reset
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Revoke portal access for this customer?')) {
                  revokeMutation.mutate();
                }
              }}
              className="rounded-xl border border-red-200 px-3 py-2 text-sm font-semibold text-red-700"
            >
              Revoke Access
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
